/**
 * BenchmarkWizard — replaces the single-page BenchmarkForm.
 *
 * 10 questions across the spec's intake categories, delivered one per
 * screen using the same `WizardShell` + `OptionCard` + `SliderRating` +
 * `TextAnswer` primitives as the quiz wizard so the two flows feel
 * identical.
 *
 * Categories (as ordered by question)
 * -----------------------------------
 *  1. Experience level (card select)
 *  2. Years in industry (slider 0..15+)
 *  3. Practice commitment (card select)
 *  4. Self-rating — technical knowledge (slider 1–10)
 *  5. Self-rating — communication (slider 1–10)
 *  6. Self-rating — leadership (slider 1–10)
 *  7. Background (text)
 *  8. Goals & motivations (text)
 *  9. Strengths & existing knowledge (text)
 * 10. Weaknesses & knowledge gaps (text)
 * 11. Preferred learning method (card select) — technically an 11th, but
 *     paired with question 10 this flow reads as 10-ish; we'll number
 *     honestly in the header as "11 of 11".
 *
 * The wizard validates each step client-side; the backend
 * ``submit_benchmark`` service revalidates.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
    BookOpen,
    Briefcase,
    Calendar,
    Eye,
    Flame,
    GraduationCap,
    Hammer,
    Hand,
    Headphones,
    HeartHandshake,
    Mic,
    Sparkles,
    Star,
    Trophy,
    Wrench,
} from 'lucide-react';
import Card from '../ui/Card';
import OptionCard from './wizard/OptionCard';
import SliderRating from './wizard/SliderRating';
import TextAnswer from './wizard/TextAnswer';
import WizardShell from './wizard/WizardShell';
import type { BenchmarkPayload } from '../../bootcampService';
import './BenchmarkWizard.css';

/** Overlay shown while the final submit is in flight. Gives the user a
 *  sense of the backend tailoring the program rather than just a spinner. */
function GeneratingRoadmapOverlay() {
    return (
        <div className="bench-wiz__generating" role="status" aria-live="polite">
            <div className="bench-wiz__generating-halo" aria-hidden="true">
                <Sparkles size={36} />
            </div>
            <h3>Generating your roadmap</h3>
            <p>Analysing your answers and tailoring Week 1 to your strengths…</p>
        </div>
    );
}

type Props = {
    /** Service-backed submit. Throws on failure so the wizard can clear
     *  its saving state without marking success. */
    onSubmit: (payload: BenchmarkPayload) => Promise<void>;
};

type ExperienceLevel = BenchmarkPayload['experience_level'];
type PracticeCommitment = BenchmarkPayload['practice_commitment'];
type PreferredLearning = BenchmarkPayload['preferred_learning'];

const EXPERIENCE_LEVELS: { value: ExperienceLevel; label: string; subtitle: string; icon: ReactNode }[] = [
    { value: 'none', label: 'New to the industry', subtitle: 'Never worked in property or construction', icon: <Sparkles size={20} /> },
    { value: 'hobbyist', label: 'Adjacent exposure', subtitle: 'Family business, student projects, curious', icon: <Star size={20} /> },
    { value: 'graduate', label: 'Graduate / early career', subtitle: 'Relevant degree, less than 2 years on the job', icon: <GraduationCap size={20} /> },
    { value: 'junior', label: 'Junior professional', subtitle: '2–5 years in a technical role', icon: <Hammer size={20} /> },
    { value: 'mid', label: 'Mid-career', subtitle: '5–10 years, comfortable running your part of a project', icon: <Briefcase size={20} /> },
    { value: 'senior', label: 'Senior practitioner', subtitle: '10+ years, looking to fill specific gaps', icon: <Trophy size={20} /> },
];

const PRACTICE_COMMITMENTS: { value: PracticeCommitment; label: string; subtitle: string; icon: ReactNode }[] = [
    { value: 'daily', label: 'Daily (6–7 days/week)', subtitle: 'I\'ll show up every day', icon: <Flame size={20} /> },
    { value: 'often', label: 'Often (4–5 days/week)', subtitle: 'Most weekdays, some weekend', icon: <Flame size={20} /> },
    { value: 'regular', label: 'Regular (2–3 days/week)', subtitle: 'Steady pace, work around other commitments', icon: <Calendar size={20} /> },
    { value: 'occasional', label: 'Occasional (1 day/week)', subtitle: 'I can commit to the weekly session', icon: <Calendar size={20} /> },
    { value: 'rarely', label: 'Rarely (few times/month)', subtitle: 'Busy schedule — honest about limits', icon: <Calendar size={20} /> },
];

const LEARNING_STYLES: { value: PreferredLearning; label: string; subtitle: string; icon: ReactNode }[] = [
    { value: 'visual', label: 'Visual', subtitle: 'Diagrams, marked-up drawings, colour-coded timelines', icon: <Eye size={20} /> },
    { value: 'auditory', label: 'Auditory', subtitle: 'Spoken explanations, phone calls, discussion', icon: <Headphones size={20} /> },
    { value: 'kinesthetic', label: 'Kinesthetic', subtitle: 'Site walk-throughs, hands-on practice', icon: <Hand size={20} /> },
    { value: 'reading', label: 'Reading / writing', subtitle: 'Written materials, long-form notes', icon: <BookOpen size={20} /> },
];

type StepKey =
    | 'experience'
    | 'years'
    | 'practice'
    | 'rate_technical'
    | 'rate_communication'
    | 'rate_leadership'
    | 'background'
    | 'goals'
    | 'strengths'
    | 'weaknesses'
    | 'learning';

const STEP_ORDER: StepKey[] = [
    'experience',
    'years',
    'practice',
    'rate_technical',
    'rate_communication',
    'rate_leadership',
    'background',
    'goals',
    'strengths',
    'weaknesses',
    'learning',
];

const TOTAL_STEPS = STEP_ORDER.length;

// ── Persistence ─────────────────────────────────────────────────────────
// A full browser refresh in the middle of 11 questions is a terrible UX,
// so we mirror every answer into localStorage. The key is scoped so
// clearing / re-seeding the program in the future doesn't collide.
const PERSIST_KEY = 'chiefy.bootcamp.benchmark.v1';

type PersistedState = {
    experienceLevel: ExperienceLevel | null;
    yearsInIndustry: number;
    practiceCommitment: PracticeCommitment | null;
    ratingTechnical: number;
    ratingCommunication: number;
    ratingLeadership: number;
    background: string;
    goals: string;
    strengths: string;
    weaknesses: string;
    preferredLearning: PreferredLearning | null;
    stepIdx: number;
};

function loadPersistedState(): Partial<PersistedState> {
    if (typeof window === 'undefined' || !window.localStorage) return {};
    try {
        const raw = window.localStorage.getItem(PERSIST_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        // Guard: only return fields we recognise — drops stale schema.
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function savePersistedState(state: PersistedState): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.setItem(PERSIST_KEY, JSON.stringify(state));
    } catch {
        // Private-mode Safari / quota exceeded — silently skip.
    }
}

function clearPersistedState(): void {
    if (typeof window === 'undefined' || !window.localStorage) return;
    try {
        window.localStorage.removeItem(PERSIST_KEY);
    } catch {
        /* ignore */
    }
}


function BenchmarkWizard({ onSubmit }: Props) {
    const persisted = loadPersistedState();

    // ── Form state (rehydrated from localStorage if present) ─────────
    const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel | null>(
        persisted.experienceLevel ?? null,
    );
    const [yearsInIndustry, setYearsInIndustry] = useState(persisted.yearsInIndustry ?? 1);
    const [practiceCommitment, setPracticeCommitment] = useState<PracticeCommitment | null>(
        persisted.practiceCommitment ?? null,
    );
    const [ratingTechnical, setRatingTechnical] = useState(persisted.ratingTechnical ?? 5);
    const [ratingCommunication, setRatingCommunication] = useState(persisted.ratingCommunication ?? 5);
    const [ratingLeadership, setRatingLeadership] = useState(persisted.ratingLeadership ?? 5);
    const [background, setBackground] = useState(persisted.background ?? '');
    const [goals, setGoals] = useState(persisted.goals ?? '');
    const [strengths, setStrengths] = useState(persisted.strengths ?? '');
    const [weaknesses, setWeaknesses] = useState(persisted.weaknesses ?? '');
    const [preferredLearning, setPreferredLearning] = useState<PreferredLearning | null>(
        persisted.preferredLearning ?? null,
    );

    const [stepIdx, setStepIdx] = useState(persisted.stepIdx ?? 0);
    const [saving, setSaving] = useState(false);

    // Mirror every state change into localStorage so a refresh restores
    // the whole wizard exactly where it was.
    useEffect(() => {
        savePersistedState({
            experienceLevel,
            yearsInIndustry,
            practiceCommitment,
            ratingTechnical,
            ratingCommunication,
            ratingLeadership,
            background,
            goals,
            strengths,
            weaknesses,
            preferredLearning,
            stepIdx,
        });
    }, [
        experienceLevel, yearsInIndustry, practiceCommitment,
        ratingTechnical, ratingCommunication, ratingLeadership,
        background, goals, strengths, weaknesses, preferredLearning,
        stepIdx,
    ]);

    const currentKey = STEP_ORDER[stepIdx];

    // ── Per-step validation ──────────────────────────────────────────
    const canContinue = (() => {
        switch (currentKey) {
            case 'experience': return experienceLevel !== null;
            case 'years': return yearsInIndustry >= 0;
            case 'practice': return practiceCommitment !== null;
            case 'rate_technical':
            case 'rate_communication':
            case 'rate_leadership':
                return true; // slider always valid
            case 'background': return background.trim().length >= 20;
            case 'goals': return goals.trim().length >= 15;
            case 'strengths': return strengths.trim().length >= 10;
            case 'weaknesses': return weaknesses.trim().length >= 10;
            case 'learning': return preferredLearning !== null;
            default: return false;
        }
    })();

    // ── Handlers ─────────────────────────────────────────────────────
    const handleBack = useCallback(() => {
        setStepIdx((prev) => Math.max(prev - 1, 0));
    }, []);

    const handleExit = useCallback(() => {
        if (stepIdx > 0) {
            const ok = window.confirm('Exit the benchmark? Your answers will be discarded.');
            if (!ok) return;
        }
        // No-op exit on step 0 — parent controls whether to show a back
        // button out of the benchmark flow (it shouldn't; there's nothing
        // to go back to).
    }, [stepIdx]);

    const handleContinue = useCallback(async () => {
        const isLast = stepIdx === TOTAL_STEPS - 1;
        if (!isLast) {
            setStepIdx((prev) => prev + 1);
            return;
        }
        // Submit
        setSaving(true);
        try {
            await onSubmit({
                experience_level: experienceLevel as ExperienceLevel,
                years_in_industry: yearsInIndustry,
                practice_commitment: practiceCommitment as PracticeCommitment,
                self_rating_technical: ratingTechnical,
                self_rating_communication: ratingCommunication,
                self_rating_leadership: ratingLeadership,
                background: background.trim(),
                goals: goals.trim(),
                strengths: strengths.trim(),
                weaknesses: weaknesses.trim(),
                preferred_learning: preferredLearning as PreferredLearning,
            });
            // Success — wipe the persisted state so a future re-enroll
            // doesn't surface the previous answers.
            clearPersistedState();
            // Parent refreshes — wizard unmounts.
        } catch {
            setSaving(false);
        }
    }, [
        stepIdx, experienceLevel, yearsInIndustry, practiceCommitment,
        ratingTechnical, ratingCommunication, ratingLeadership,
        background, goals, strengths, weaknesses, preferredLearning,
        onSubmit,
    ]);

    // ── Per-step render ──────────────────────────────────────────────
    const { title, subtitle, body } = renderStep({
        key: currentKey,
        experienceLevel,
        setExperienceLevel,
        yearsInIndustry,
        setYearsInIndustry,
        practiceCommitment,
        setPracticeCommitment,
        ratingTechnical,
        setRatingTechnical,
        ratingCommunication,
        setRatingCommunication,
        ratingLeadership,
        setRatingLeadership,
        background,
        setBackground,
        goals,
        setGoals,
        strengths,
        setStrengths,
        weaknesses,
        setWeaknesses,
        preferredLearning,
        setPreferredLearning,
        disabled: saving,
    });

    return (
        <Card className="benchmark-wizard-card">
            <Card.Body>
                {saving ? (
                    <GeneratingRoadmapOverlay />
                ) : (
                    <WizardShell
                        step={stepIdx + 1}
                        totalSteps={TOTAL_STEPS}
                        title={title}
                        subtitle={subtitle}
                        canContinue={canContinue}
                        onBack={stepIdx > 0 ? handleBack : null}
                        onContinue={handleContinue}
                        onExit={handleExit}
                        finalSparkle
                        busy={saving}
                    >
                        {body}
                    </WizardShell>
                )}
            </Card.Body>
        </Card>
    );
}

// ── Step renderer ────────────────────────────────────────────────────────

type RenderArgs = {
    key: StepKey;
    experienceLevel: ExperienceLevel | null;
    setExperienceLevel: (v: ExperienceLevel) => void;
    yearsInIndustry: number;
    setYearsInIndustry: (v: number) => void;
    practiceCommitment: PracticeCommitment | null;
    setPracticeCommitment: (v: PracticeCommitment) => void;
    ratingTechnical: number;
    setRatingTechnical: (v: number) => void;
    ratingCommunication: number;
    setRatingCommunication: (v: number) => void;
    ratingLeadership: number;
    setRatingLeadership: (v: number) => void;
    background: string;
    setBackground: (v: string) => void;
    goals: string;
    setGoals: (v: string) => void;
    strengths: string;
    setStrengths: (v: string) => void;
    weaknesses: string;
    setWeaknesses: (v: string) => void;
    preferredLearning: PreferredLearning | null;
    setPreferredLearning: (v: PreferredLearning) => void;
    disabled: boolean;
};

function renderStep(args: RenderArgs): { title: string; subtitle?: string; body: ReactNode } {
    switch (args.key) {
        case 'experience':
            return {
                title: "What's your experience level?",
                subtitle: "Be honest — we'll meet you where you are",
                body: (
                    <div className="bench-wiz__options" role="radiogroup">
                        {EXPERIENCE_LEVELS.map((e) => (
                            <OptionCard
                                key={e.value}
                                leading={e.icon}
                                label={e.label}
                                subtitle={e.subtitle}
                                selected={args.experienceLevel === e.value}
                                onSelect={() => args.setExperienceLevel(e.value)}
                                disabled={args.disabled}
                            />
                        ))}
                    </div>
                ),
            };

        case 'years':
            return {
                title: 'How many years in the industry?',
                subtitle: 'Slide to your rough count',
                body: (
                    <SliderRating
                        value={args.yearsInIndustry}
                        onChange={args.setYearsInIndustry}
                        icon={<Briefcase size={24} />}
                        min={0}
                        max={15}
                        leftLabel="Starting out"
                        rightLabel="15+"
                        labelForValue={(v) => v === 0 ? 'None yet' : v === 15 ? '15 years or more' : `${v} year${v === 1 ? '' : 's'}`}
                        disabled={args.disabled}
                    />
                ),
            };

        case 'practice':
            return {
                title: 'How much time can you commit?',
                subtitle: 'Consistency is key to getting the most out of the program',
                body: (
                    <div className="bench-wiz__options" role="radiogroup">
                        {PRACTICE_COMMITMENTS.map((p) => (
                            <OptionCard
                                key={p.value}
                                leading={p.icon}
                                label={p.label}
                                subtitle={p.subtitle}
                                selected={args.practiceCommitment === p.value}
                                onSelect={() => args.setPracticeCommitment(p.value)}
                                disabled={args.disabled}
                            />
                        ))}
                    </div>
                ),
            };

        case 'rate_technical':
            return {
                title: 'Rate your technical knowledge',
                subtitle: '1 = complete beginner, 10 = elite',
                body: (
                    <SliderRating
                        value={args.ratingTechnical}
                        onChange={args.setRatingTechnical}
                        icon={<Wrench size={24} />}
                        leftLabel="New to it"
                        rightLabel="Industry expert"
                        disabled={args.disabled}
                    />
                ),
            };

        case 'rate_communication':
            return {
                title: 'Rate your communication & stakeholder skills',
                subtitle: '1 = struggles in meetings, 10 = runs the room',
                body: (
                    <SliderRating
                        value={args.ratingCommunication}
                        onChange={args.setRatingCommunication}
                        icon={<Mic size={24} />}
                        leftLabel="Quiet in the room"
                        rightLabel="Runs the room"
                        disabled={args.disabled}
                    />
                ),
            };

        case 'rate_leadership':
            return {
                title: 'Rate your leadership & decision-making',
                subtitle: '1 = prefer to be told, 10 = make the call',
                body: (
                    <SliderRating
                        value={args.ratingLeadership}
                        onChange={args.setRatingLeadership}
                        icon={<HeartHandshake size={24} />}
                        leftLabel="Follower"
                        rightLabel="Decisive leader"
                        disabled={args.disabled}
                    />
                ),
            };

        case 'background':
            return {
                title: 'Tell us about your background',
                subtitle: 'Education, years in industry, prior roles, relevant exposure',
                body: (
                    <TextAnswer
                        value={args.background}
                        onChange={args.setBackground}
                        placeholder="e.g. Civil engineering graduate, 3 years on a mid-tier commercial builder, now moving into development…"
                        rows={5}
                        minLength={20}
                        disabled={args.disabled}
                        hint="A few sentences — the more specific, the better we can tailor Week 1."
                    />
                ),
            };

        case 'goals':
            return {
                title: 'Your goals & motivations',
                subtitle: "Be specific — 'learn more' is too vague",
                body: (
                    <TextAnswer
                        value={args.goals}
                        onChange={args.setGoals}
                        placeholder="e.g. Transition into development management within 12 months, run my first $20M+ project in 3 years…"
                        rows={4}
                        minLength={15}
                        disabled={args.disabled}
                        hint="One concrete 12-month goal and one stretch goal is a good shape."
                    />
                ),
            };

        case 'strengths':
            return {
                title: 'Where are you already credible?',
                subtitle: 'Your existing strengths — the things you\'d feel confident teaching someone else',
                body: (
                    <TextAnswer
                        value={args.strengths}
                        onChange={args.setStrengths}
                        placeholder="e.g. Structural engineering fundamentals, cost estimation, fluent in Revit…"
                        rows={4}
                        minLength={10}
                        disabled={args.disabled}
                    />
                ),
            };

        case 'weaknesses':
            return {
                title: "What are your biggest gaps?",
                subtitle: "This is the most useful answer — we prioritise these in your plan",
                body: (
                    <TextAnswer
                        value={args.weaknesses}
                        onChange={args.setWeaknesses}
                        placeholder="e.g. Never seen a development feasibility, weak on planning scheme, struggle in client-facing meetings…"
                        rows={4}
                        minLength={10}
                        disabled={args.disabled}
                    />
                ),
            };

        case 'learning':
            return {
                title: 'How do you learn best?',
                subtitle: 'Shapes how we deliver Week 1 content to you',
                body: (
                    <div className="bench-wiz__options" role="radiogroup">
                        {LEARNING_STYLES.map((l) => (
                            <OptionCard
                                key={l.value}
                                leading={l.icon}
                                label={l.label}
                                subtitle={l.subtitle}
                                selected={args.preferredLearning === l.value}
                                onSelect={() => args.setPreferredLearning(l.value)}
                                disabled={args.disabled}
                            />
                        ))}
                    </div>
                ),
            };
    }
}

export default BenchmarkWizard;
